import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Chip,
  CircularProgress,

  Snackbar,
  Alert,
} from "@mui/material";
import api from "../../utils/api";

export default function AgentPayoutHistory() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  // server pagination
  const [page, setPage] = useState(0); // MUI is 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // requery loading (per row)
  const [busyId, setBusyId] = useState(null);

  // toast
  const [toast, setToast] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/agent/payouts/history", {
        params: {
          page: page + 1,
          limit: rowsPerPage,
        },
      });

      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error("Failed to load payout history", err);
      setRows([]);
      setTotal(0);
      setToast({
        open: true,
        severity: "error",
        message: "Failed to load payout history.",
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // ===============================
  // RE-QUERY REJECTED PAYOUT
  // ===============================
  const requery = async (id) => {
    if (busyId) return;

    const ok = window.confirm(
      "Re-query this rejected payout? Admin will review it again."
    );
    if (!ok) return;

    try {
      setBusyId(id);
      await api.post(`/agent/payouts/${id}/requery`);

      setToast({
        open: true,
        severity: "success",
        message: "Payout re-queried successfully.",
      });

      // refresh list
      await loadHistory();
    } catch (err) {
      console.error("Re-query failed", err);
      setToast({
        open: true,
        severity: "error",
        message:
          err?.response?.data?.message ||
          "Failed to re-query payout. Please try again.",
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        Payout History
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={2}>
        View all your payout requests and payment status.
      </Typography>

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4 }}>
            <Typography>No payout history yet.</Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Amount (₦)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      ₦{Number(row.amount_naira).toLocaleString()}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.status}
                        color={
                          row.status === "paid"
                            ? "success"
                            : row.status === "approved"
                            ? "primary"
                            : row.status === "rejected"
                            ? "error"
                            : "default"
                        }
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      {new Date(row.requested_at).toLocaleDateString()}
                    </TableCell>

                    <TableCell>
                      {row.paid_at
                        ? new Date(row.paid_at).toLocaleDateString()
                        : "-"}
                    </TableCell>

                    <TableCell>{row.payment_reference || "-"}</TableCell>
                    <TableCell>{row.note || "-"}</TableCell>

                    {/* ACTION */}
                    <TableCell align="right">
					
                     {/*   {row.status === "rejected" ? (
					  
                      <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          disabled={busyId === row.id}
                          onClick={() => requery(row.id)}
                        >
                          {busyId === row.id ? "Re-querying…" : "Re-Query"}
                        </Button>
					
						
                      ) : (
                        "-"
                      )}  	*/}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Paper>

      {/* TOAST */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast({ ...toast, open: false })}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
